import { DialogComponent } from "@components/Dialog";
import { LoadingButton } from "@mui/lab";
import { Box, Button, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { CircleX } from "lucide-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";
import { useSnackbar } from "notistack";
import { MAX_FILES } from "../../utils/constants";
import { fileListItemStyle, getBrowseButtonStyle, getDropzoneBoxStyle, getUploadButtonStyle } from "@pages/askDost/styles/UploadModalStyle";

interface IAskDostUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
  isUploading: boolean;
  translate: (text: string, options?: any) => string;
  fullScreen?: boolean;
}

export const AskDostUploadModal = ({
  isOpen,
  onClose,
  onUpload,
  isUploading,
  translate,
  fullScreen,
}: IAskDostUploadModalProps) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [modalFiles, setModalFiles] = useState<File[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isValidFile = (file: File) => {
    const fileName = file.name.toLowerCase();
    return fileName.endsWith('.pdf') || fileName.endsWith('.csv') || fileName.endsWith('.xlsx');
  };

  const handleClose = () => {
    setModalFiles([]);
    setErrorMessage(null);
    onClose();
  };

  const handleRemoveFile = (indexToRemove: number) => {
    setModalFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
    setErrorMessage(null);
  };

  const handleModalUpload = () => {
    if (modalFiles.length > 0) {
      onUpload(modalFiles);
    }
    handleClose();
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    multiple: true,
    noClick: true,
    onDrop: (acceptedFiles) => {
      const validFiles = acceptedFiles.filter(isValidFile);
      const invalidFiles = acceptedFiles.filter((file) => !isValidFile(file));
      invalidFiles.forEach((file) => {
        enqueueSnackbar(translate("invalid_file_type", { fileName: file.name }), {
          variant: "error",
        });
      });

      const remainingSlots = MAX_FILES - modalFiles.length;

      if (validFiles.length > remainingSlots) {
        setErrorMessage(translate("upload_limit_exceeded"));
      } else {
        setErrorMessage(null);
      }

      if (remainingSlots > 0) {
        const filesToAdd = validFiles.slice(0, remainingSlots);
        setModalFiles((prev) => [...prev, ...filesToAdd]);
      }
    }
  });

  return (
    <DialogComponent
      dialogTitle={translate("upload_files_for_ask_dost")}
      dialogSubTitle={translate("you_can_attach_files_in_your_prompts_to_ask_dost_for_it_to_analyse_them")}
      isShowModal={isOpen}
      showModalMethod={handleClose}
      fullScreen={fullScreen}
      fullWidth={true}
      maxWidth="sm"
      dialogContent={
        <Box sx={{ width: "100%", mt: 2 }}>
          <Box {...getRootProps()} sx={getDropzoneBoxStyle(theme, isDragActive)}>
            <Typography variant="h6" fontWeight={600} color={theme.palette.text.heading}>
              {translate("upload_your_documents")}
            </Typography>
            
            <Typography variant="body2" color={theme.palette.text.label} sx={{ mb: 2 }}>
              {translate("drag_and_drop_your_files_here")}
            </Typography>

            <Button
              variant="contained"
              onClick={open}
              disabled={modalFiles.length >= MAX_FILES}
              sx={getBrowseButtonStyle(theme)}
            >
              {translate("browse_files")}
            </Button>

            <input {...getInputProps()} accept=".pdf,.csv,.xlsx" />

            <Typography
              variant="caption"
              color={theme.palette.text.label}
              sx={{ mt: 2, display: "block", maxWidth: "80%" }}
            >
              {translate("only_pdf_csv_excel_files_accepted")}
            </Typography>

            {errorMessage && (
              <Typography
                variant="caption"
                color={theme.palette.error.main}
                sx={{ display: "block", fontWeight: 500 }}
              >
                {errorMessage}
              </Typography>
            )}
          </Box>

          {/* Files List */}
          {modalFiles.length > 0 && (
            <Stack spacing={2} sx={{ mt: 4, maxHeight: "150px", overflowY: "auto" }}>
              {modalFiles.map((file, index) => (
                <Box key={index} sx={fileListItemStyle}>
                  <Typography variant="body2" noWrap sx={{ maxWidth: "80%" }}>
                    {file.name}
                  </Typography>
                  <IconButton size="small" onClick={() => handleRemoveFile(index)}>
                    <CircleX size={16} color={theme.palette.error.main} />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      }
      dialogActions={
        <Stack direction="row" justifyContent="flex-end" sx={{ marginTop: "12px" }} alignItems="center">
          <LoadingButton
            loading={isUploading}
            variant="contained"
            onClick={handleModalUpload}
            disabled={modalFiles.length === 0}
            sx={getUploadButtonStyle(theme)}
          >
            {translate("upload")}
          </LoadingButton>
        </Stack>
      }
    />
  );
};